import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { attachmentsApi } from 'attachment/api';
import { ERROR_MESSAGES } from 'common/constants';
import { assert, getImageFormData } from 'common/utils';
import { mapMemberToApi } from 'community/mappers';
import { communityIdFromApi } from 'domain/CommunityId';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import orderBy from 'lodash/fp/orderBy';
import { axiosBaseQuery, getApiBaseUrl, mapToApiParams, transformPaginatedApiPayload } from 'modules/api/utils';

import { mapCommunityFromApi, mapCommunityToApi } from './mappers';

import type {
  EventValidationResult,
  GetCommunityAuthenticationMethodsResponse,
  GetCommunityMembers,
  GetCommunityMembersParams,
  GetMyCommunitiesPayload,
  JoinCommunityAsMemberArgs,
  UpdateCommunityBrandingPayload,
} from './types';
import type { ImageFile } from 'common/components/ImageDropzoneWithPreview/types';
import type { PaginatedApiPayload } from 'domain/Common';
import type {
  Community,
  CommunityAdminToggleRequest,
  CommunityApiPayload,
  CommunityFormValues,
  CommunityGuest,
  CommunityMember,
  CommunityMemberFormData,
  CommunityMemberSocials,
  CommunitySettings,
  UpdateCommunityData,
} from 'domain/Community';
import type { CommunityId } from 'domain/CommunityId';

export const communityApi = createApi({
  reducerPath: 'communityApi',
  baseQuery: retry(axiosBaseQuery(), { maxRetries: 0 }),
  tagTypes: ['MY_COMMUNITIES', 'COMMUNITY_MEMBER', 'ALL_COMMUNITY_MEMBERS'],
  endpoints: (builder) => ({
    createCommunity: builder.mutation<{ id: CommunityId }, CommunityFormValues>({
      query: (data) => ({
        url: `/community`,
        method: 'POST',
        data: mapCommunityToApi(data),
      }),
      transformResponse: ({ id }: Pick<CommunityApiPayload, 'id'>) => ({ id: communityIdFromApi(id) }),
    }),
    getMyCommunities: builder.query<GetMyCommunitiesPayload, void>({
      keepUnusedDataFor: 600,
      query: () => `/community/me/joined`,
      providesTags: ['MY_COMMUNITIES'],
      transformResponse: (response: PaginatedApiPayload<CommunityApiPayload>) => {
        const { data = [], totalCount } = transformPaginatedApiPayload(response);

        return {
          myCommunities: flow(
            map(mapCommunityFromApi),
            orderBy(({ name }) => name.toLocaleLowerCase(), 'asc')
          )(data) as Community[],
          hasNoCommunity: !totalCount,
        };
      },
    }),
    uploadLogo: builder.mutation<
      Pick<Community, 'logoImageLink' | 'logoImageLinkSmall'>,
      { logo: ImageFile; communityId: CommunityId }
    >({
      query: ({ communityId, logo }) => ({
        url: `/community/${communityId}/uploadLogo`,
        method: 'POST',
        data: getImageFormData(logo),
      }),
    }),
    updateMyCommunity: builder.mutation<Community, UpdateCommunityData>({
      query: (data) => ({
        url: `/community/${data.id}`,
        method: 'PUT',
        data: mapCommunityToApi(data),
      }),
      // we don't invalidate MY_COMMUNITIES here as in some places we fire this call multiple times
      // and we want to refetch data on demand, only when we are done with all the updates (see updateCommunityBranding)
      invalidatesTags: ['COMMUNITY_MEMBER'],
    }),
    getCommunityMembers: builder.query<PaginatedApiPayload<CommunityMember>, GetCommunityMembersParams>({
      query: ({
        page,
        pageSize,
        sortBy,
        sortOrder,
        showAdmins,
        tagFilters,
        searchPhrase,
        communityId,
        roles,
        includeCallingUser = true,
      }) => {
        const hasTagFilters = tagFilters && Object.values(tagFilters).some((v) => v.length > 0);
        return {
          baseURL: getApiBaseUrl('v2'),
          url: `/community/${communityId}/members`,
          params: mapToApiParams({
            page,
            size: pageSize,
            sortParam: sortBy,
            sortDir: sortOrder,
            // TODO change to displayOnlyAdmins after BE is ready
            displayOnlyManagers: showAdmins,
            includeCallingUser,
            tagFilters: hasTagFilters ? JSON.stringify(tagFilters) : undefined,
            roleIds: map('id')(roles),
            searchText: searchPhrase.length ? searchPhrase : undefined,
          }) as GetCommunityMembers,
        };
      },
      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ id }) => ({ type: 'ALL_COMMUNITY_MEMBERS' as const, id })), 'ALL_COMMUNITY_MEMBERS']
          : [],
    }),
    inviteGuestsToCommunity: builder.mutation<void, { guests: CommunityGuest[]; communityId: CommunityId | undefined }>(
      {
        query: ({ communityId, guests }) => {
          assert(communityId, ERROR_MESSAGES.noCommunityId);
          return {
            url: `/community/${communityId}/members`,
            method: 'PUT',
            data: guests,
          };
        },
        invalidatesTags: ['ALL_COMMUNITY_MEMBERS'],
      }
    ),
    resendInvitation: builder.mutation<void, { communityId: CommunityId; memberId: number }>({
      query: ({ communityId, memberId }) => ({
        url: `/community/${communityId}/members/${memberId}/resend-invitation`,
        method: 'POST',
      }),
    }),
    removeCommunityMember: builder.mutation<
      void,
      { memberId: number; communityId: CommunityId | undefined; displayName: string }
    >({
      query: ({ communityId, memberId }) => {
        assert(communityId, ERROR_MESSAGES.noCommunityId);
        return {
          url: `/community/${communityId}/${memberId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['ALL_COMMUNITY_MEMBERS'],
    }),
    getCurrentCommunityMember: builder.query<CommunityMember, { communityId: CommunityId | undefined }>({
      keepUnusedDataFor: 600,
      query: ({ communityId }) => {
        assert(communityId, ERROR_MESSAGES.noCommunityId);
        return `/community/${communityId}/me`;
      },
      providesTags: ['COMMUNITY_MEMBER'],
    }),
    updateCurrentCommunityMember: builder.mutation<
      void,
      {
        communityId: CommunityId;
        data: Partial<CommunityMemberFormData>;
        currentData: CommunityMember | undefined;
      }
    >({
      query: ({ communityId, data, currentData }) => ({
        url: `/community/${communityId}/members/me`,
        method: 'PATCH',
        data: mapMemberToApi(currentData, data),
      }),
      invalidatesTags: ['COMMUNITY_MEMBER'],
    }),
    updateMySocials: builder.mutation<void, { socials: Partial<CommunityMemberSocials>; communityId: CommunityId }>({
      query: ({ communityId, socials }) => ({
        url: `/community/${communityId}/members/me/socials`,
        method: 'PATCH',
        data: socials,
      }),
      invalidatesTags: ['COMMUNITY_MEMBER'],
    }),
    getCronofyAuthenticateUrl: builder.query<void, void>({
      query: () => ({
        url: '/cronofy/authenticate',
      }),
    }),
    submitCronofyCode: builder.mutation<void, { code: string }>({
      // TODO: uncomment below and remove code from listener.ts starting line 39 when BE fixed
      // invalidatesTags: ['COMMUNITY_MEMBER'],
      query: ({ code }) => ({
        url: '/cronofy/code',
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        data: code,
      }),
    }),
    registerToCommunity: builder.mutation<CommunityMember, { communityId: CommunityId }>({
      query: ({ communityId }) => ({
        url: `/community/${communityId}/invitation/register`,
        method: 'PATCH',
      }),
      invalidatesTags: ['MY_COMMUNITIES', 'COMMUNITY_MEMBER'],
    }),
    // Self-join a community as a member (v2). `jobTitle` is only persisted by the backend on the
    // membership-creating call (idempotent no-op if already a member). Used by useEnterEvent to
    // satisfy the 403 NOT_COMMUNITY_MEMBER gate before registering/attending an event.
    //
    // Deliberately does NOT invalidate COMMUNITY_MEMBER: this is only reached from the 403 path, so
    // the user is not yet a member of the target community and there is no `/me` cache entry for it
    // to refresh. The freshly-joined community's `/me` is fetched via the active-community switch
    // (setActiveCommunityId → useAuthorization re-subscribes). Since COMMUNITY_MEMBER is an id-less
    // tag, invalidating it here would only refetch the *previously* active community's `/me` — a
    // wasted call for a membership that didn't change.
    joinCommunityAsMember: builder.mutation<void, JoinCommunityAsMemberArgs>({
      query: ({ communityId, jobTitle }) => ({
        baseURL: getApiBaseUrl('v2'),
        url: `/community/${communityId}/members`,
        method: 'POST',
        data: { jobTitle },
      }),
      invalidatesTags: ['MY_COMMUNITIES'],
    }),
    toggleCommunityAdmin: builder.mutation<void, CommunityAdminToggleRequest>({
      query: ({ accountId, isPromote, communityId }) => ({
        url: `/community/${communityId}/community-managers/${accountId}`,
        method: 'PATCH',
        params: {
          // TODO change to grantCommunityAdminRole after BE is ready
          grantCommunityManagerRole: isPromote,
        },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'ALL_COMMUNITY_MEMBERS', id: accountId }],
    }),
    validateEventDate: builder.mutation<EventValidationResult, { date: Date; communityId: CommunityId }>({
      query: ({ communityId, date }) => ({
        url: `/community/${communityId}/validate-event`,
        method: 'POST',
        data: {
          startDate: date,
        },
      }),
    }),
    updateCommunityBranding: builder.mutation<CommunityId, UpdateCommunityBrandingPayload>({
      queryFn: async (data, { dispatch }) => {
        const { communityId, logo, defaultEventImage, deleteDefaultEventImageAttachmentId, ...payload } = data;
        assert(communityId, ERROR_MESSAGES.noCommunityId);

        if (defaultEventImage) {
          const attachment = await dispatch(
            attachmentsApi.endpoints.uploadAttachment.initiate(defaultEventImage)
          ).unwrap();
          payload.defaultEventImageAttachmentId = attachment.id;
        }

        // @ts-expect-error UpdateCommunityData type is deprecated
        await dispatch(communityApi.endpoints.updateMyCommunity.initiate({ id: communityId, ...payload })).unwrap();

        if (deleteDefaultEventImageAttachmentId) {
          const attachmentId = deleteDefaultEventImageAttachmentId;
          await dispatch(attachmentsApi.endpoints.deleteAttachment.initiate({ id: attachmentId })).unwrap();
        }

        if (logo) {
          await dispatch(communityApi.endpoints.uploadLogo.initiate({ communityId, logo })).unwrap();
        }

        dispatch(communityApi.util.invalidateTags(['MY_COMMUNITIES']));

        return { data: communityId };
      },
    }),
    updateCommunitySettings: builder.mutation<void, { community: Community; settings: Partial<CommunitySettings> }>({
      queryFn: async ({ community, settings }, { dispatch }) => {
        await dispatch(
          communityApi.endpoints.updateMyCommunity.initiate({
            ...community,
            communitySettings: { ...community.communitySettings, ...settings },
          } as unknown as UpdateCommunityData)
        );

        return { data: undefined };
      },
      invalidatesTags: ['MY_COMMUNITIES'],
    }),
    getCommunityAuthenticationMethods: builder.query<GetCommunityAuthenticationMethodsResponse, { slug: string }>({
      query: ({ slug }) => `/community/slug/${slug}`,
    }),
    getCommunityAuthenticationMethodsById: builder.query<
      GetCommunityAuthenticationMethodsResponse,
      { id: string | undefined }
    >({
      query: ({ id }) => `/community/${id}/public`,
    }),
  }),
});

export const {
  useGetMyCommunitiesQuery,
  useLazyGetMyCommunitiesQuery,
  useGetCurrentCommunityMemberQuery,
  useUpdateCurrentCommunityMemberMutation,
  useLazyGetCronofyAuthenticateUrlQuery,
  useRegisterToCommunityMutation,
  useJoinCommunityAsMemberMutation,
  useToggleCommunityAdminMutation,
  useGetCommunityMembersQuery,
  useRemoveCommunityMemberMutation,
  useResendInvitationMutation,
  useInviteGuestsToCommunityMutation,
  useUpdateMyCommunityMutation,
  useUploadLogoMutation,
  useLazyGetCommunityMembersQuery,
  useUpdateMySocialsMutation,
  useCreateCommunityMutation,
  useSubmitCronofyCodeMutation,
  useValidateEventDateMutation,
  useUpdateCommunityBrandingMutation,
  useUpdateCommunitySettingsMutation,
  useGetCommunityAuthenticationMethodsQuery,
  useGetCommunityAuthenticationMethodsByIdQuery,
} = communityApi;
